import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './index';
import { preferencesApi } from '../services/preferencesApi';

// User preferences interface - Phase 1: Core preferences
export interface UserPreferences {
  showErrorConsole: boolean;
  showNotifications: boolean;
  // Phase 2: Extended preferences (adding theme support)
  theme: 'light' | 'dark' | 'system';
  fontSize: 'small' | 'medium' | 'large';
  compactMode: boolean;
  sidebarAutoCollapse: boolean;
  notificationSounds: boolean;
  highContrast: boolean;
  autoDataExport: boolean;
  // Optional: older stored preference blobs won't have this key; treat
  // missing as false rather than requiring a backfill.
  useNewGui?: boolean;
}

// Default preferences
export const defaultPreferences: UserPreferences = {
  showErrorConsole: false, // Error console hidden by default
  showNotifications: true,  // Notifications shown by default
  // Extended defaults
  theme: 'system',          // Follow system preference
  fontSize: 'medium',       // Standard font size
  compactMode: false,       // Normal spacing
  sidebarAutoCollapse: false, // Keep sidebar open
  notificationSounds: true, // Play notification sounds
  highContrast: false,      // Normal contrast
  autoDataExport: false,    // Manual export by default
  useNewGui: false          // Redesigned GUI opt-in; only takes effect when NEW_GUI_ENABLED system setting is also on
};

// Preferences state interface
interface PreferencesState {
  data: UserPreferences | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  lastSynced: string | null;
}

// Initial state
const initialState: PreferencesState = {
  data: null,
  loading: false,
  saving: false,
  error: null,
  lastSynced: null,
};

// Async thunks for API calls
export const loadUserPreferences = createAsyncThunk(
  'preferences/loadUserPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const data = await preferencesApi.getUserPreferences();
      return {
        data,
        lastSynced: new Date().toISOString(),
      };
    } catch (error: any) {
      // For any API failure, silently fall back to defaults (don't log errors)
      // This prevents console spam while maintaining functionality
      console.debug('Preferences API unavailable, using defaults (this is normal)');
      return {
        data: defaultPreferences,
        lastSynced: new Date().toISOString(),
      };
    }
  }
);

export const saveUserPreferences = createAsyncThunk(
  'preferences/saveUserPreferences',
  async (preferences: UserPreferences, { rejectWithValue }) => {
    try {
      const data = await preferencesApi.saveUserPreferences(preferences);
      return {
        data,
        lastSynced: new Date().toISOString(),
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save preferences');
    }
  }
);

export const resetUserPreferences = createAsyncThunk(
  'preferences/resetUserPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const data = await preferencesApi.resetUserPreferences();
      return {
        data,
        lastSynced: new Date().toISOString(),
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to reset preferences');
    }
  }
);

// Preferences slice
const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    // Update a single preference (immediate UI update)
    updatePreference: (
      state,
      action: PayloadAction<{ key: keyof UserPreferences; value: any }>
    ) => {
      if (state.data) {
        state.data = {
          ...state.data,
          [action.payload.key]: action.payload.value,
        };
      }
    },

    // Clear error state
    clearError: (state) => {
      state.error = null;
    },

    // Load from localStorage (fallback)
    loadFromLocalStorage: (state) => {
      try {
        const stored = localStorage.getItem('user_preferences');
        if (stored) {
          const parsed = JSON.parse(stored);
          state.data = { ...defaultPreferences, ...parsed };
        } else {
          state.data = defaultPreferences;
        }
      } catch (error) {
        console.warn('Failed to load preferences from localStorage:', error);
        state.data = defaultPreferences;
      }
    },

    // Save to localStorage (immediate persistence)
    saveToLocalStorage: (state) => {
      if (state.data) {
        try {
          localStorage.setItem('user_preferences', JSON.stringify(state.data));
        } catch (error) {
          console.warn('Failed to save preferences to localStorage:', error);
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Load preferences
    builder
      .addCase(loadUserPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUserPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.lastSynced = action.payload.lastSynced;
        state.error = null;
      })
      .addCase(loadUserPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Fallback to localStorage if API fails
        const stored = localStorage.getItem('user_preferences');
        if (stored) {
          try {
            state.data = JSON.parse(stored);
          } catch {
            state.data = defaultPreferences;
          }
        } else {
          state.data = defaultPreferences;
        }
      });

    // Save preferences
    builder
      .addCase(saveUserPreferences.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUserPreferences.fulfilled, (state, action) => {
        state.saving = false;
        state.data = action.payload.data;
        state.lastSynced = action.payload.lastSynced;
        state.error = null;
        // Also save to localStorage for immediate persistence
        if (action.payload.data) {
          localStorage.setItem('user_preferences', JSON.stringify(action.payload.data));
        }
      })
      .addCase(saveUserPreferences.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // Reset preferences
    builder
      .addCase(resetUserPreferences.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(resetUserPreferences.fulfilled, (state, action) => {
        state.saving = false;
        state.data = action.payload.data;
        state.lastSynced = action.payload.lastSynced;
        state.error = null;
        // Clear localStorage and set defaults
        localStorage.removeItem('user_preferences');
      })
      .addCase(resetUserPreferences.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  updatePreference,
  clearError,
  loadFromLocalStorage,
  saveToLocalStorage,
} = preferencesSlice.actions;

// Selectors
export const selectPreferences = (state: RootState) => state.preferences.data;
export const selectPreferencesLoading = (state: RootState) => state.preferences.loading;
export const selectPreferencesSaving = (state: RootState) => state.preferences.saving;
export const selectPreferencesError = (state: RootState) => state.preferences.error;
export const selectPreferencesLastSynced = (state: RootState) => state.preferences.lastSynced;

// Helper selector with defaults
export const selectUserPreferences = (state: RootState): UserPreferences => {
  return state.preferences.data || defaultPreferences;
};

export default preferencesSlice.reducer;
